import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';
import matrixService from '../services/matrixService';

/**
 * Settings Context - Global application settings
 * Manages user preferences including read receipt behavior per TI requirements
 */

const STORAGE_KEY = 'ti_app_settings';

// Default settings per TI requirements:
// 1. TI-M clients must disable the sending of public read receipts by default
// 2. TI-M clients must send private read receipts if sending public read receipts is disabled
const DEFAULT_SETTINGS = {
  // Read receipt settings
  sendPublicReadReceipts: false, // TI requirement: disabled by default
  sendPrivateReadReceipts: true, // TI requirement: enabled when public is disabled
  alwaysAppearOffline: false,
  autoLogoutTimer: 0, // Default to 0 (disabled)
  
  // Typing indicator settings
  sendTypingIndicators: true, // Enabled by default - user can disable for privacy
  
  // Future settings can be added here
  // e.g., notifications, presence, etc.
};

const SettingsContext = createContext(null);

/**
 * Provider component for application settings
 */
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Load settings from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      logger.error('Failed to load settings from localStorage:', error);
    }
    return DEFAULT_SETTINGS;
  });

  // Persist settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      logger.error('Failed to save settings to localStorage:', error);
    }
  }, [settings]);

  useEffect(() => {
    matrixService.setSendPresenceUpdates(!settings.alwaysAppearOffline);
    logger.debug(`Updating presence sharing: ${!settings.alwaysAppearOffline}`);
    if (settings.alwaysAppearOffline) {
      matrixService.setUserPresence('offline');
    } else {
      matrixService.setUserPresence();
    }
  }, [settings.alwaysAppearOffline]);

  /**
   * Update a specific setting
   * @param {string} key - Setting key
   * @param {any} value - New value
   */
  const updateSetting = useCallback((key, value) => {
    // First update the state, then do any other side effects
    // Note: Not all settings require side effects
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      
      // - When sendPublicReadReceipts = true → public receipts (m.read)
      // - When sendPublicReadReceipts = false → private receipts (m.read.private)
      switch (key) {
        case 'sendPublicReadReceipts': // Legacy support: if someone directly sets sendPrivateReadReceipts
          newSettings.sendPrivateReadReceipts = !value;
          if (value) {
            logger.info('Switched to public read receipts (m.read)');
          } else {
            logger.info('Switched to private read receipts (m.read.private)');
          }
          break;
        case 'sendPrivateReadReceipts':
          newSettings.sendPublicReadReceipts = !value;
          if (value) {
            logger.info('Switched to private read receipts (m.read.private)');
          } else {
            logger.info('Switched to public read receipts (m.read)');
          }
          break;
      }

      return newSettings;
    });
    window.dispatchEvent(new CustomEvent('settings-changed'));
  }, []);

  /**
   * Update multiple settings at once
   * @param {Object} updates - Object with setting keys and values
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
    window.dispatchEvent(new CustomEvent('settings-changed'));
  }, []);

  /**
   * Reset all settings to defaults
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    logger.info('Settings reset to defaults');
  }, []);

  /**
   * Get the appropriate receipt type based on current settings
   * @returns {'m.read'|'m.read.private'|null} Receipt type to use, or null if receipts disabled
   */
  const getReceiptType = useCallback(() => {
    if (settings.sendPublicReadReceipts) {
      return 'm.read';
    }
    if (settings.sendPrivateReadReceipts) {
      return 'm.read.private';
    }
    return null; // No receipts enabled (shouldn't happen per TI requirements)
  }, [settings.sendPublicReadReceipts, settings.sendPrivateReadReceipts]);

  const value = {
    settings,
    updateSetting,
    updateSettings,
    resetSettings,
    getReceiptType,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access settings context
 * @returns {Object} Settings context value
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

/**
 * Hook to get just the receipt type for convenience
 * @returns {'m.read'|'m.read.private'|null} Current receipt type
 */
export const useReceiptType = () => {
  const { getReceiptType } = useSettings();
  return getReceiptType();
};
